import {
  addRevision,
  hideRevisionsUnlessSaving,
  populateRevisions,
  populateStudents,
  reviewStudent,
  saveStatus,
  switchToStudent,
} from './modules/assignment-ui.ts';
import { setBrowsing } from './modules/browse-mode.ts';
import { $, withClass } from './modules/dom.ts';
import editorTabs from './modules/editor-tabs.ts';
import {
  type AssignmentConfig,
  type Message,
  ReportedError,
  type SiteMetadata,
  type TestResults,
} from './modules/evaluator-types.ts';
import {
  bootstrapFailed,
  describe,
  getJson,
  patchJson,
  postJson,
  reportErrors,
} from './modules/fetch-helpers.ts';
import genericTesting from './modules/generic-testing.ts';
import inject from './modules/inject.ts';
import interactiveJavaEvaluator from './modules/interactive-java.ts';
import interactiveJavaGuiEvaluator from './modules/interactive-java-gui.ts';
import interactiveJavaTestedEvaluator from './modules/interactive-java-tested.ts';
import interactivePythonEvaluator from './modules/interactive-python.ts';
import javascriptEvaluator from './modules/javascript-evaluator.ts';
import Login from './modules/login.ts';
import { pageConfig } from './modules/page-config.ts';
import {
  fetchPairingStatus,
  partnersLabel,
  showConflictBanner,
  showPairingIndicator,
  showStashBanner,
} from './modules/pairing-ui.ts';
import pythonEvaluator from './modules/python-evaluator.ts';
import serverEvaluator from './modules/server-evaluator.ts';
import { pageLocation, type SaveResult, ServerStorage, type Storage } from './modules/storage.ts';
import testing from './modules/testing.ts';
import witnessedJsEvaluator from './modules/witnessed-js-evaluator.ts';

// The slice of config.json this entry point itself reads, over and above the
// shared AssignmentConfig keys the evaluators consume.
type PageConfig = AssignmentConfig & {
  files: string[];
  gui?: boolean;
  canvas?: boolean;
  graphic?: boolean;
  testing?: { id: string; casesId: string };
  genericTesting?: { id: string; casesId: string };
  // Selector of an element to hold a bare pass/fail mark instead of a
  // results table (a java-demo page graded on what its program prints).
  resultMark?: string;
  // Student projects (plans/student-projects.md): the run target is a
  // movable pin (the row's main_file, riding in as the one configured
  // file) rather than a fixed configured main — wire the pin flow.
  pinnableMain?: boolean;
};

// The surface this page drives on whichever evaluator pickEvaluator chooses.
interface Evaluator {
  onServerResults?: ((results: TestResults) => void) | null;
  execute(
    code: string,
    file: string,
    others: { file: string; content: string }[],
  ): Promise<unknown>;
}

// How a run's results get shown: the testing modules' pill displays, or the
// canvas/graphic injectors, or nothing.
interface ResultDisplay {
  update(result: unknown): void;
  // Called as a run starts, for displays that should drop a stale verdict.
  reset?(): void;
  allPassed?: boolean;
}

// A JS unit-test assignment: tested in the browser via the run-tests.js
// harness and the testcases.js next to the page. The explicit marker is
// `handler: "unit-test"` (plans/batch-grading-reconciliation.md phase 5);
// the testcases.js-in-extraFiles sniff stays as a fallback for content
// published before the handler key existed.
const hasJsTests = (config: PageConfig): boolean =>
  config.testing !== undefined &&
  (config.handler === 'unit-test' ||
    (config.extraFiles ?? []).some((f) => f.split('/').pop() === 'testcases.js'));

const makeEvaluator = (
  config: PageConfig,
  message: Message,
  username: string,
  metadata: SiteMetadata,
): Evaluator => {
  if (config.gui) return interactiveJavaGuiEvaluator(config, message, username, metadata);
  if (config.interactive) {
    if (config.language !== 'java') {
      return interactivePythonEvaluator(config, message, username, metadata);
    }
    // Interactive AND unit-test graded (java-terminal-testcases.njk): the
    // Terminal tab runs the program, the Tests tab grades it.
    return config.server
      ? interactiveJavaTestedEvaluator(
          config as PageConfig & { server: NonNullable<PageConfig['server']> },
          message,
          username,
          metadata,
        )
      : interactiveJavaEvaluator(config, message, username, metadata);
  }
  if (config.server) {
    // Checking config.server doesn't narrow config itself, so assert the
    // narrowing the server evaluator's signature wants.
    return serverEvaluator(
      config as PageConfig & { server: NonNullable<PageConfig['server']> },
      message,
      username,
      metadata,
    );
  }
  if (config.language === 'python') return pythonEvaluator(config, message, username, metadata);
  if (config.language === 'javascript' && hasJsTests(config)) {
    return witnessedJsEvaluator(config, message, username, metadata);
  }
  return javascriptEvaluator(config, message, username, metadata);
};

////////////////////////////////////////////////////////////////////////////////
// UI manipulations

// Every message replaces the minibuffer's class as well as its text: a plain
// "Job complete." after a red "Compile error." comes up in the normal color
// again, not in the error color the last message left behind.
const message = (text: string, fade?: number, clazz?: string) => {
  const minibuffer = $('.minibuffer') as HTMLElement;
  minibuffer.innerText = text;
  minibuffer.title = text;
  minibuffer.className = clazz ? `toolbar minibuffer ${clazz}` : 'toolbar minibuffer';
  if (fade) {
    setTimeout(() => {
      if (minibuffer.innerText === text) message('');
    }, fade);
  }
};

// End UI manipulations
////////////////////////////////////////////////////////////////////////////////

const getSpeedrun = (storage: Storage) => {
  const {
    speedrunId,
    speedrunFirstSha: sha,
    speedrunStartedAt: startedAt,
  } = ($('body') as HTMLElement).dataset;
  return {
    speedrunId,
    sha,
    startedAt,
    inSpeedrun() {
      return this.speedrunId;
    },
    async finish(abandon = false, onFinish?: () => void) {
      // The clock can stop optimistically (the finishing run already
      // happened); the Finished!/ABANDONED banner can't — it's painted only
      // once the server has recorded the run, so a failed PATCH never hides
      // behind a success banner.
      ($('#clock') as HTMLElement).classList.add('stopped');
      if ($('#abandon-speedrun')) {
        ($('#abandon-speedrun') as HTMLElement).remove();
      }

      let r: Response;
      try {
        const { sha, timestamp } = await storage.latestCommit();
        r = await patchJson(`/api/${abandon ? 'abandon' : 'finish'}-speedrun`, {
          ...this,
          sha,
          finishedAt: timestamp,
        });
      } catch (e) {
        // Same rule as the non-2xx path below: say so, keep the speedrun
        // state so a later run can still record it.
        alert(`${describe(e, 'record your speedrun')}. Run your code again to retry.`);
        return;
      }
      if (!r.ok) {
        // Don't fail silently and don't clear the speedrun state: another
        // finish (e.g. re-running after logging back in) can still record it.
        alert(
          r.status === 401
            ? 'Your speedrun was NOT recorded — you are logged out. Log in (another tab is fine) and run your code again.'
            : `Your speedrun could not be recorded (HTTP ${r.status}). Run your code again to retry.`,
        );
        return;
      }
      if (abandon) {
        ($('.speedrun-title') as HTMLElement).append(
          withClass('abandoned', $('<span>', 'ABANDONED')),
        );
      } else {
        ($('.speedrun-title') as HTMLElement).append(withClass('done', $('<span>', 'Finished!')));
      }
      this.sha = undefined;
      this.speedrunId = undefined;
      delete ($('body') as HTMLElement).dataset.speedrunId;
      delete ($('body') as HTMLElement).dataset.speedrunFirstSha;
      if ($('#revisions-container')) {
        ($('#revisions-container') as HTMLElement).hidden = false;
      }
      if (onFinish) onFinish();
    },
  };
};

const setup = async () => {
  // Top level information about the site.
  const metadata = await getJson<SiteMetadata>('/metadata.json');

  // Get the per-page configuration that tells us things like what code to
  // load — the config.json next to the page, or the inline copy a
  // per-viewer page (a student project) renders instead.
  const config = await pageConfig<PageConfig>();

  const tabs = editorTabs('editor', config);

  const login = new Login(metadata);
  // The browser-only surfaces (demos, /m/ material previews) are detected
  // inside makeStorage so the Snap! and form entry points get the same
  // storage and notice.
  const storage = await login.makeStorage();
  hideRevisionsUnlessSaving(storage);
  const speedrun = getSpeedrun(storage);

  // Not sure if we want to use this. It's a bit extreme. Also not clear that
  // the beforeonload event works for this and if it doesn't this would be easy
  // to circumvent by closing the tab, switching to another tab, and then
  // reopening the speedrun. (I guess I could make it so if you load the page
  // and the speedrun is already inprogress (rather than just beginning) we
  // abandon it immediately. But sometimes we may want to reload the page.

  // if (speedrun.inSpeedrun()) {
  //   const maybeAbandon = () => {
  //     // Check inSpeedrun again because it can change if we finish
  //     if (speedrun.inSpeedrun() && document.visibilityState === 'hidden') {
  //       speedrun.finish(true);
  //     }
  //   };
  //   document.onvisibilitychange = maybeAbandon;
  //   window.onbeforeunload = maybeAbandon;
  // }

  let resultDisplay: ResultDisplay;
  if (config.testing) {
    resultDisplay = testing($(config.testing.id), $(config.testing.casesId));
    resultDisplay.update({});
  } else if (config.genericTesting) {
    resultDisplay = genericTesting($(config.genericTesting.id), $(config.genericTesting.casesId));
    // make object with no properties at all. Possibly should do same above.
    resultDisplay.update(Object.create(null));
  } else if (config.resultMark) {
    // One ✅ / ❌ once a run has results (with a passed/total count when
    // there's more than one test and not all passed), cleared while a run
    // is in flight (a run that fails to compile never reaches update). No
    // table, so nothing about the expected output reaches the page.
    const mark = $(config.resultMark) as HTMLElement;
    const display: ResultDisplay = {
      allPassed: false,
      reset() {
        mark.textContent = '';
      },
      update(result: unknown) {
        const tests = Object.values(result as TestResults).map((r) => r ?? []);
        const passed = tests.filter((rs) => rs.length > 0 && rs.every((r) => r.passed)).length;
        display.allPassed = tests.length > 0 && passed === tests.length;
        const count = tests.length > 1 && !display.allPassed ? ` ${passed}/${tests.length}` : '';
        mark.textContent = (display.allPassed ? '✅' : '❌') + count;
      },
    };
    resultDisplay = display;
  } else if (config.canvas) {
    resultDisplay = {
      // The canvas flow's result is the generated JS drawing program.
      update(result: unknown) {
        inject(result as string);
      },
    };
  } else if (config.graphic) {
    resultDisplay = {
      // The graphic flow's result is the <img> markup to swap in.
      update(result: unknown) {
        ($('div.graphic img') as HTMLElement).outerHTML = result as string;
      },
    };
  } else {
    resultDisplay = { update() {} };
  }

  // login.username is null for anonymous users; the evaluator factories
  // declare `username: string` but have always tolerated null at runtime.
  const evaluator = makeEvaluator(config, message, login.username as string, metadata);

  // Code has to be the first element of the files list: for Java it's the
  // file containing the class the tester exercises; the contents of any other
  // tabs ride along as extra sources. On a pinnableMain page (a student
  // project) the configured file IS the pin, and creating the first file
  // sets it (recordPin below) — undefined until then, which is why Run
  // checks before doing anything.
  let mainFile: string | undefined = config.files[0];

  // The full set of files for this assignment: the configured files (in
  // config order, defining the leading tabs) plus any files the student has
  // added in a previous session, discovered from the repo directory.
  const allFiles = async () => {
    const discovered = (await storage.listFiles())
      .filter((f: string) => !config.files.includes(f))
      .sort();
    return [...config.files, ...discovered];
  };

  // The last content each tab loaded or saved — "is the editor clean?" for
  // the pair-mode freshness poll (a dirty editor is never silently
  // refreshed; the save's baseSha check handles that path).
  const lastPersisted = new Map<string, string>();
  const rememberPersisted = (files: { file: string; content: string }[]) => {
    for (const { file, content } of files) lastPersisted.set(file, content);
  };
  const editorClean = () =>
    tabs.contents().every(({ file, content }) => lastPersisted.get(file) === content);

  // Put code in the editor tabs. Don't evaluate it immediately in case we've
  // saved something that doesn't work (e.g. contains an infinite loop that
  // crashes the page.)
  const fillTabs = (files: string[], contents: Record<string, string>) => {
    for (const file of files) {
      tabs.open(file, contents[file]);
      lastPersisted.set(file, contents[file]);
    }
  };

  // Save feedback shown next to the Run button (see generic-frame.njk).
  const status = saveStatus();

  // Browse mode (plans/delete-rename-files.md): the sha being viewed, or
  // null when the editor shows (editable) latest. Declared up here because
  // the pin control and Run consult it; the machinery itself is below.
  let browsingSha: string | null = null;

  // The run pin (plans/student-projects.md): on a pinnableMain page the
  // "runs <file>" control shows and moves the pin, and the first file the
  // student creates pins itself. The local main moves optimistically (so
  // the very first Run works without a reload); the row update lands in the
  // background with any failure shown, never swallowed.
  const pinControl = $('#main-file-control') as HTMLElement | null;
  const pinSelect = pinControl?.querySelector('select') as HTMLSelectElement | null;

  const refreshPinControl = () => {
    if (!pinControl || !pinSelect) return;
    const files = tabs.files();
    pinControl.hidden = files.length === 0;
    pinSelect.replaceChildren(...files.map((f) => new Option(f, f, false, f === mainFile)));
    pinSelect.disabled = Boolean(reviewStudent()) || browsingSha !== null;
  };

  // null CLEARS the pin — the project's last file was deleted, and a stale
  // pin would resurrect it as a stub on the next load
  // (plans/delete-rename-files.md).
  const recordPin = (file: string | null) => {
    mainFile = file ?? undefined;
    refreshPinControl();
    // Never write into a project we're only reviewing (the server refuses
    // ?student= pins anyway; the control is disabled above).
    if (reviewStudent()) return;
    postJson('main-file', { file }).then(
      (r) => {
        if (!r.ok) status.message(`Couldn’t set the file to run (${r.status})`);
      },
      (e: unknown) => status.message(describe(e, 'set the file to run')),
    );
  };

  // When a background retry lands work buffered during a server outage.
  storage.onBufferFlushed = (f) => {
    if (f.commit) {
      addRevision(f.commit);
      status.saved(f.commit.author.date);
    } else {
      status.noChanges();
    }
  };

  // Tracks the most recent in-flight save so that speedrun finish/abandon
  // can wait for it before calling latestCommit().
  let savePromise: Promise<unknown> = Promise.resolve();

  // Pair mode (plans/pair-code-sharing.md): who we're working with, for the
  // conflict banner and freshness poll below. Set up after the tabs load.
  let pairPartnerLabel: string | null = null;

  // Refresh every open tab to the pair directory's current saved state
  // (after a partner's save): pinned at `sha` so the snapshot is atomic.
  const refreshToRevision = async (sha: string) => {
    if (!(storage instanceof ServerStorage)) return;
    await Promise.all(
      tabs.files().map(async (file) => {
        const content = await storage.loadAtRevision(file, sha).catch((e: unknown) => {
          if ((e as { status?: number }).status === 404) return '';
          throw e;
        });
        tabs.setContent(file, content);
        lastPersisted.set(file, content);
      }),
    );
    storage.noteDirSha(sha);
    storage.allRevisions().then(populateRevisions);
  };

  // A save bounced off a partner's newer work: their version and ours both
  // exist (ours only in this editor), someone has to pick. "Save mine
  // anyway" first stashes THEIR version's shas via noteDirSha so the retry
  // commits on top of it — nothing is erased either way, the revisions
  // dropdown keeps both. conflictShowing keeps the pushed partner-save
  // events (pair mode, below) from re-raising the banner under the
  // student while it's already asking.
  let conflictShowing = false;
  const handleConflict = (conflict: { latestSha: string | null }) => {
    conflictShowing = true;
    showConflictBanner({
      partnerLabel: pairPartnerLabel ?? 'Your partner',
      onLoadTheirs: () => {
        conflictShowing = false;
        // Stash the editor's unsaved version in this browser (offered back
        // by the stash banner on the next visit), and drop its buffered
        // copies — the buffer would otherwise background-flush OUR version
        // right back over the partner's work we just chose to keep.
        for (const { file, content } of tabs.contents()) {
          try {
            localStorage.setItem(`conflict-stash:${pageLocation().pagePath}/${file}`, content);
          } catch {
            // quota — the stash is best-effort
          }
        }
        if (storage instanceof ServerStorage) storage.discardBuffered(tabs.files());
        if (conflict.latestSha) {
          refreshToRevision(conflict.latestSha).then(() =>
            status.message(`Loaded ${pairPartnerLabel ?? 'your partner'}’s latest version`),
          );
        }
      },
      onKeepMine: () => {
        conflictShowing = false;
        if (storage instanceof ServerStorage) storage.noteDirSha(conflict.latestSha);
        saveTabs();
      },
    });
  };

  const handleSaveResult = (f: SaveResult) => {
    if (f.commit) {
      addRevision(f.commit);
      status.saved(f.commit.author.date);
      rememberPersisted(tabs.contents());
    } else if (f.conflict) {
      status.message('Not saved — your partner saved a newer version');
      handleConflict(f.conflict);
    } else if (f.buffered) {
      if (f.authLost) {
        status.authLost();
      } else {
        status.message('Server unreachable — work kept in this browser, retrying…');
      }
    } else if (f.local) {
      status.message('Saved in this browser only');
    } else {
      status.noChanges();
      rememberPersisted(tabs.contents());
    }
  };

  const saveTabs = () => {
    status.saving();
    savePromise = storage
      .saveAll(tabs.contents())
      .then(handleSaveResult)
      .catch((e: unknown) => {
        console.error('Save failed:', e);
        status.failed();
      });
    return savePromise;
  };

  // ---- Crash/close safety net ----------------------------------------------
  //
  // Run is the only deliberate save on these pages, so work typed since the
  // last run used to vanish, silently, whenever the page went away — close
  // the tab, navigate, shut the lid, or (no unload events at all) Chrome
  // killing the tab under memory pressure. Three layers, none of which
  // changes what a version in the revisions menu means:
  //
  // - a write-through timer keeps dirty tab contents in the storage buffer
  //   (localStorage), so even a tab kill loses at most BUFFER_WRITE_MS of
  //   typing — the buffer commits on the next visit (or rides along with the
  //   next save), and the page says it recovered work;
  // - hiding the tab (switching away, closing the lid) does a real save, so
  //   the work reaches the server, not just this browser;
  // - beforeunload buffers synchronously — a fetch started during teardown
  //   can be aborted, and its failure handler (where saveAll's own buffering
  //   lives) never runs on a dead page; localStorage can't be taken away.
  //
  // Never while reviewing (the tabs hold another student's code) or browsing
  // an old revision (stale content must not be recommitted as new work) —
  // the same guards as Run's save.
  const BUFFER_WRITE_MS = 15_000;
  const bufferDirtyWork = () => {
    if (reviewStudent() || browsingSha !== null) return;
    const dirty = tabs
      .contents()
      .filter(({ file, content }) => lastPersisted.get(file) !== content);
    if (dirty.length > 0) storage.bufferNow(dirty);
  };
  if (!reviewStudent()) {
    window.setInterval(bufferDirtyWork, BUFFER_WRITE_MS);
    window.addEventListener('beforeunload', bufferDirtyWork);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'hidden') return;
      bufferDirtyWork();
      if (browsingSha === null && !editorClean()) saveTabs();
    });
  }

  // ---- File delete/rename (plans/delete-rename-files.md) -------------------
  //
  // The tabs' gestures call these delegates; the tab set changes only after
  // the commit lands, so a failure leaves everything as it was. Never
  // installed while reviewing — the affordances then don't render at all.

  if (config.allowNewFiles && !reviewStudent()) {
    tabs.onDeleteFile = async (file: string) => {
      try {
        const r = await storage.deleteFile(file);
        if (r.conflict) {
          status.message('Not deleted — your partner saved a newer version');
          handleConflict(r.conflict);
          return false;
        }
        // A commit, a noop (the file was never saved), or a local delete —
        // the file is gone either way.
        tabs.close(file);
        lastPersisted.delete(file);
        if (r.commit) {
          addRevision(r.commit);
          status.saved(r.commit.author.date);
        }
        if (config.pinnableMain && mainFile === file) {
          // The pin follows: the first remaining file, or cleared.
          recordPin(tabs.files()[0] ?? null);
        } else {
          refreshPinControl();
        }
        return true;
      } catch (e) {
        status.message(describe(e, `delete ${file}`));
        return false;
      }
    };

    tabs.onRenameFile = async (from: string, to: string) => {
      try {
        // One atomic commit: the new name with the editor's current content
        // (a rename is a save) plus the old name's removal.
        const r = await storage.rename(from, to, tabs.content(from));
        if (r.conflict) {
          status.message('Not renamed — your partner saved a newer version');
          handleConflict(r.conflict);
          return false;
        }
        tabs.rename(from, to);
        lastPersisted.delete(from);
        lastPersisted.set(to, tabs.content(to));
        if (r.commit) {
          addRevision(r.commit);
          status.saved(r.commit.author.date);
        }
        if (config.pinnableMain && mainFile === from) {
          recordPin(to);
        } else {
          refreshPinControl();
        }
        return true;
      } catch (e) {
        status.message(describe(e, `rename ${from}`));
        return false;
      }
    };
  }

  // ---- Revision browsing (plans/delete-rename-files.md) --------------------
  //
  // Picking an old revision shows exactly the files and contents of that
  // moment; dirty work is committed first, so nothing is ever lost. While
  // browsing, saves are suppressed (Run executes the visible code, off the
  // record — the evaluators fold browse: true into their submissions) and
  // the explicit Restore makes the directory exactly the browsed snapshot.

  const revisionsSelect = $('#file-revisions') as HTMLSelectElement | null;
  // Options are newest-first (populateRevisions appends in log order,
  // addRevision prepends), so the first option is the latest revision.
  const newestSha = () => revisionsSelect?.options[0]?.value ?? null;
  const saveStatusEl = $('.editor-toolbar .save-status') as HTMLElement | null;
  // On a pinnableMain page the one configured file is just the pin; real
  // configured files keep a (blanked) tab even when the snapshot predates
  // them, so the page machinery always has its main file.
  const configuredFiles: string[] = config.pinnableMain ? [] : config.files;

  // Reconcile the tabs to exactly the snapshot at `sha`: open its files,
  // close managed tabs that didn't exist yet, fill every tab's content as of
  // that commit (blank where the file didn't exist).
  const showRevisionView = async (sha: string) => {
    const snapshot = new Set(await storage.listFilesAt(sha));
    for (const f of tabs.files()) {
      if (!snapshot.has(f) && !configuredFiles.includes(f)) tabs.close(f);
    }
    for (const f of snapshot) {
      if (!tabs.has(f)) tabs.open(f, '');
    }
    await Promise.all(
      tabs.files().map(async (f) => {
        const content = snapshot.has(f)
          ? await storage.loadAtRevision(f, sha).catch((e: unknown) => {
              if ((e as { status?: number }).status === 404) return '';
              throw e;
            })
          : '';
        tabs.setContent(f, content);
      }),
    );
  };

  const setBrowseUi = (sha: string | null) => {
    browsingSha = sha;
    setBrowsing(sha !== null); // the evaluators' off-the-record flag
    tabs.setBrowsing(sha !== null);
    if (saveStatusEl) saveStatusEl.hidden = sha !== null;
    if (browseBanner) {
      browseBanner.hidden = sha === null;
      if (sha !== null) {
        const option = [...(revisionsSelect?.options ?? [])].find((o) => o.value === sha);
        (browseBanner.querySelector('.browse-label') as HTMLElement).textContent =
          `Viewing ${option?.innerText ?? 'old revision'}`;
      }
    }
    refreshPinControl();
  };

  const leaveBrowseMode = async () => {
    const newest = newestSha();
    if (browsingSha === null || newest === null) return;
    await showRevisionView(newest);
    // The latest revision's content is committed content: a clean editor.
    rememberPersisted(tabs.contents());
    setBrowseUi(null);
    if (revisionsSelect) revisionsSelect.value = newest;
  };

  const restoreBrowsedRevision = async () => {
    if (browsingSha === null || reviewStudent()) return;
    // Restore what's on screen (edits made while browsing included): a sync
    // commit — the directory becomes exactly these files, deletions of
    // later-created files included, baseSha-guarded.
    const r = await storage.restore(tabs.contents());
    if (r.conflict) {
      // Someone (a partner, another tab) committed since this page last
      // looked; never wipe that blind. Catch up and land on the latest —
      // the browsed snapshot is still one dropdown pick away.
      if (storage instanceof ServerStorage) storage.noteDirSha(r.conflict.latestSha);
      await storage
        .allRevisions()
        .then(populateRevisions)
        .catch(() => {});
      await leaveBrowseMode();
      status.message('Not restored — newer work was saved since; showing the latest.');
      return;
    }
    if (r.commit) {
      addRevision(r.commit);
      status.saved(r.commit.author.date);
    } else {
      status.noChanges(); // the snapshot was identical to the latest
    }
    rememberPersisted(tabs.contents());
    setBrowseUi(null);
    const newest = newestSha();
    if (revisionsSelect && newest) revisionsSelect.value = newest;
    // A restore can remove the pinned file (created after the snapshot).
    if (config.pinnableMain && (mainFile === undefined || !tabs.has(mainFile))) {
      recordPin(tabs.files()[0] ?? null);
    }
  };

  const switchToRevision = async (sha: string) => {
    if (!revisionsSelect) return;
    const newest = newestSha();
    if (sha === newest) {
      if (browsingSha !== null) await leaveBrowseMode();
      return;
    }
    if (browsingSha === null && !reviewStudent() && !editorClean()) {
      // Entering browse from dirty work: commit it first — it becomes the
      // newest revision, one pick away — and don't switch if it can't land.
      status.saving();
      const r = await storage.saveAll(tabs.contents());
      handleSaveResult(r);
      if (!r.commit && !r.noChanges) {
        // Buffered (offline) or conflict: handleSaveResult said so; stay.
        if (newest) revisionsSelect.value = newest;
        return;
      }
      // handleSaveResult's addRevision selected the new commit's option;
      // put the selection back on the revision being browsed.
      revisionsSelect.value = sha;
    }
    await showRevisionView(sha);
    setBrowseUi(sha);
  };

  // The banner standing in for the save status while browsing: what's being
  // viewed, the way back, and the deliberate write.
  const browseBanner = ((): HTMLElement | null => {
    const container = $('#revisions-container') as HTMLElement | null;
    if (!container || !revisionsSelect || !storage.saves) return null;
    const banner = document.createElement('span');
    banner.id = 'browse-banner';
    banner.hidden = true;
    const label = document.createElement('span');
    label.className = 'browse-label';
    banner.append(label);
    const back = document.createElement('button');
    back.type = 'button';
    back.textContent = 'Back to latest';
    back.onclick = reportErrors('go back to the latest revision', () => leaveBrowseMode());
    banner.append(back);
    if (!reviewStudent()) {
      const restore = document.createElement('button');
      restore.type = 'button';
      restore.textContent = 'Restore this version';
      restore.title = 'Make this snapshot your latest saved version';
      restore.onclick = reportErrors('restore this version', () => restoreBrowsedRevision());
      banner.append(restore);
    }
    container.after(banner);
    return banner;
  })();

  // Show a run's results and, in a speedrun, finish when everything passes.
  // Called with the browser run's results as soon as they're in, and again
  // by the witnessed-js evaluator if the server's results disagree with
  // them (so a divergent-but-passing witnessed run can still finish the
  // speedrun).
  const applyResults = async (result: unknown) => {
    // undefined means the run has nothing to say about tests (a terminal run
    // on an interactive+tested page): leave the display's last results alone.
    if (result === undefined) return;
    resultDisplay.update(result);
    if (resultDisplay.allPassed && speedrun.inSpeedrun()) {
      await savePromise;
      speedrun.finish(false, () => storage.allRevisions().then(populateRevisions));
    }
  };
  evaluator.onServerResults = applyResults;

  // Evaluate code now in editor and also save it. All tabs are saved in one
  // commit; the main file's contents are what get evaluated, with the other
  // tabs along for the ride as extra sources.
  const reevaluateCode = () => {
    // A pinnableMain page with no files yet has nothing to run.
    if (mainFile === undefined) {
      message('Create a file first, with the + button in the tab bar.', 5000);
      return;
    }
    const main = mainFile;
    const files = tabs.contents();
    const code = tabs.content(main);
    const others = files.filter((f) => f.file !== main);

    // Never save into a student's work we're only reviewing, and never save
    // while browsing an old revision — running the visible code is half the
    // point of browsing, but it's a view, not work: the run records nothing
    // (the evaluators carry browse: true) and Restore is the deliberate
    // write (plans/delete-rename-files.md).
    if (browsingSha !== null) {
      // The banner is already saying what's being viewed.
    } else if (!reviewStudent()) {
      saveTabs();
    } else {
      status.message("Read-only — viewing a student's copy");
    }

    resultDisplay.reset?.();
    evaluator.execute(code, main, others).then(applyResults, (e: unknown) => {
      // The evaluators report their own runner failures (a ReportedError —
      // the minibuffer already says "Compile error." and the pane has the
      // details); this catches what they don't — e.g. a data file or extra
      // source that won't load — so a run never dies silently with the Run
      // button looking idle.
      if (e instanceof ReportedError) return;
      message(describe(e, 'run your code'), undefined, 'error');
    });
  };

  // If storage is unreachable (server hiccup, session expired mid-page) fall
  // back to the committed web starters so the editor still works, and say so
  // — never leave a dead page with an uncaught rejection.
  const tabsLoaded = allFiles()
    .then((files: string[]) =>
      storage.ensureStarters(files).then((contents: Record<string, string>) => {
        fillTabs(files, contents);
        if (storage.saves) {
          storage.allRevisions().then(populateRevisions);
        }
      }),
    )
    .catch(async (e: unknown) => {
      console.error('Loading saved work failed; falling back to starter code:', e);
      status.message('Couldn’t load saved work — showing starter code');
      const contents: Record<string, string> = {};
      await Promise.all(
        config.files.map((file: string) =>
          storage
            .loadFromWeb(file)
            .then((code: string) => {
              contents[file] = code;
            })
            .catch(() => {
              contents[file] = '';
            }),
        ),
      );
      fillTabs(config.files, contents);
    });

  // Work buffered by a previous visit (an outage, or the crash safety net
  // above) is already on screen — load() prefers it — and the storage layer
  // is flushing it in the background; say so, so the recovery is visible
  // rather than the work just silently being back.
  tabsLoaded.then(() => {
    if (storage.hasBufferedWork()) {
      status.message('Recovered unsaved work from your last visit — saving it now…');
    }
  });

  // A conflict stash from an earlier session ("Load their version" kept the
  // student's own unsaved version on this computer): offer it back, so the
  // stash is a way back rather than a write-only hole. Restore puts it in
  // the tabs and saves — in pair mode that save can 409 into the ordinary
  // conflict flow, which is exactly the conversation to have.
  if (!reviewStudent() && storage.saves) {
    tabsLoaded.then(() => {
      const stashPrefix = `conflict-stash:${pageLocation().pagePath}/`;
      const stashKeys = Object.keys(localStorage).filter((k) => k.startsWith(stashPrefix));
      if (stashKeys.length === 0) return;
      const clearStash = () => {
        for (const k of stashKeys) localStorage.removeItem(k);
      };
      showStashBanner({
        onRestore: () => {
          for (const k of stashKeys) {
            const content = localStorage.getItem(k);
            if (content === null) continue;
            const file = k.slice(stashPrefix.length);
            if (tabs.has(file)) {
              tabs.setContent(file, content);
            } else {
              tabs.open(file, content);
            }
          }
          clearStash();
          saveTabs();
        },
        onDiscard: clearStash,
      });
    });
  }

  if (config.pinnableMain) {
    // New files pin themselves when there's no pin yet; otherwise they just
    // join the control's list.
    tabs.onAddFile = (file: string) => {
      if (mainFile === undefined) {
        recordPin(file);
      } else {
        refreshPinControl();
      }
    };
    if (pinSelect) {
      pinSelect.onchange = () => recordPin(pinSelect.value);
    }
    tabsLoaded.then(() => {
      // Self-heal a missing pin: files exist (created in a session whose
      // pin never landed) but the config carried none — the first tab
      // becomes the pin.
      if (mainFile === undefined && tabs.files().length > 0) {
        recordPin(tabs.files()[0]);
      } else {
        refreshPinControl();
      }
    });
  }

  if (storage.saves) {
    // pageLocation, not a \w+ regex: course ids can contain hyphens, and a
    // failed parse silently fetched /api/undefined/students (an empty picker).
    const course = pageLocation().courseId ?? undefined;
    if (!reviewStudent() && speedrun.inSpeedrun()) {
      ($('#clock') as HTMLElement).dataset.timestamp = speedrun.startedAt;
      ($('#abandon-speedrun') as HTMLElement).onclick = async () => {
        await savePromise;
        speedrun.finish(true, () => storage.allRevisions().then(populateRevisions));
      };
    }
    // A failed picker load must not abort setup before Run is wired.
    await populateStudents(course, reviewStudent()).catch((e: unknown) =>
      status.message(describe(e, 'load the student list')),
    );

    // Pair mode (plans/pair-code-sharing.md): if this user is paired for
    // this assignment, show who with, guard saves with the directory's
    // latest-known sha (the conflict banner above), and keep a clean editor
    // quietly in step with the partner's saves. Teachers reviewing see the
    // indicator only.
    if (storage instanceof ServerStorage) {
      const loc = pageLocation();
      if (loc.courseId && loc.dir) {
        const pairing = await fetchPairingStatus(loc.courseId, loc.dir, storage.reviewing);
        if (pairing.unknown) status.message(pairing.unknown);
        if (pairing.paired) {
          pairPartnerLabel = partnersLabel(pairing.partners);
          showPairingIndicator(pairing, Boolean(storage.reviewing));
          if (!pairing.detached && !storage.reviewing) {
            // Baseline AFTER the load flow so the starters' ifAbsent commit
            // (which bypasses the save path's sha tracking) is already in
            // the history we read; only then do saves carry baseSha.
            await tabsLoaded;
            await storage.allRevisions().catch(() => {});
            storage.enablePairMode();
            storage.onConflict = handleConflict;

            // The directory moved to `sha` (a pushed event, or the sweep
            // below): follow it when the editor is clean, raise the
            // conflict banner when it isn't — a partner saving over local
            // edits is a conflict the moment it happens, not at this
            // student's next save. A browsed view is never touched, and
            // waiting out savePromise keeps our own commits (whose events
            // also arrive here) from reading as a partner's.
            const onDirMoved = async (sha: string) => {
              if (browsingSha !== null || conflictShowing) return;
              await savePromise;
              if (browsingSha !== null || conflictShowing) return;
              if (sha === storage.dirSha) return;
              if (!editorClean()) {
                handleConflict({ latestSha: sha });
                return;
              }
              await refreshToRevision(sha);
              status.message(`Updated with ${pairPartnerLabel}’s latest work`);
            };

            // Fast path: the server pushes every commit in this directory
            // (modules/code-events.ts) the moment it lands. The first
            // event on every (re)connect carries the current sha — the
            // catch-up — so a deploy or wifi blip costs nothing once
            // EventSource redials on its own.
            let streamOk = false;
            const events = new EventSource(`/api/code-events/${loc.courseId}/${loc.dir}`);
            events.onopen = () => {
              streamOk = true;
            };
            events.onerror = () => {
              streamOk = false;
            };
            events.onmessage = (e) => {
              void (async () => {
                try {
                  const { sha } = JSON.parse(e.data) as { sha: string | null };
                  if (sha) await onDirMoved(sha);
                } catch {
                  // Malformed or transient: the sweep below catches up.
                }
              })();
            };

            // The backstop sweep (the git store's own webhook-plus-sweep
            // pattern): the only mechanism while the stream is down, a
            // slow correctness check while it's up.
            const PAIR_POLL_MS = 30_000;
            const SWEEP_EVERY = 10; // ≈ every 5 minutes with the stream up
            let tick = 0;
            window.setInterval(async () => {
              if (document.hidden) return;
              tick++;
              if (streamOk && tick % SWEEP_EVERY !== 0) return;
              try {
                const latest = await storage.latestRevision();
                if (latest) await onDirMoved(latest.sha);
              } catch {
                // Transient poll failures just wait for the next tick.
              }
            }, PAIR_POLL_MS);
          }
        }
      }
    }
  }

  ($('.editor-toolbar .run-code') as HTMLElement).onclick = reevaluateCode;

  ($('.minibuffer') as HTMLElement).onclick = () => message('');

  if (revisionsSelect) {
    revisionsSelect.onchange = reportErrors('switch revisions', () =>
      switchToRevision(revisionsSelect.value),
    );
  }

  if ($('#student-selector')) {
    ($('#student-selector') as HTMLSelectElement).onchange = (e) => {
      switchToStudent((e.target as HTMLSelectElement).value);
    };
  }
};

setup().catch(bootstrapFailed);
